/**
 * The Quality overview's sections (contract v3): Overview · Release gate · Does it work? · Is it safe? · Can I prove
 * it? · Settings (useQualitySection: the section in view, a pick scrolls the panel to it).
 * - In the workspace, the title's last crumb ("<Project> / Verify / Quality / Overview ▾", QualitySectionCrumb): a
 *   compact menu button in the crumbs' font (portalled into WorkspaceTitle's `crumbRef`) that names the section in
 *   view while the page scrolls and lists them all; picking one scrolls there. On a phone it wraps with the crumb.
 * - Alone (QualityPanel without the workspace: component tests), the row of anchor links above the overview
 *   (QualitySectionMenu), the one in view carrying `aria-current="location"`.
 * Settings is a view of its own: picking it opens it.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Dropdown } from 'antd';
import { CaretDown } from '@phosphor-icons/react';
import { SECTIONS } from './qualityModel';
import useQualitySection from './useQualitySection';

export function QualitySectionCrumb({ onOpenSettings, slot, watch = null }) {
  const { current, go } = useQualitySection(watch);
  const [open, setOpen] = useState(false);
  const label = SECTIONS.find((s) => s.key === current)?.label || SECTIONS[0].label;
  const pick = ({ key }) => {
    setOpen(false);
    if (key === 'settings') { onOpenSettings(); return; }
    go(key);
  };
  return createPortal(
    <>
      <span aria-hidden="true" className="workspace-view-sep">/</span>
      <Dropdown
        autoFocus
        menu={{ items: SECTIONS.map((s) => ({ key: s.key, label: s.label })), onClick: pick, selectable: true, selectedKeys: [current] }}
        onOpenChange={setOpen}
        open={open}
        trigger={['click']}
      >
        <button
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={`Section: ${label}`}
          className="workspace-view-crumb-link quality-section-crumb"
          data-testid="quality-section-crumb"
          title="Go to a section of the Quality page"
          type="button"
        >
          {label}<CaretDown aria-hidden size={12} weight="bold" />
        </button>
      </Dropdown>
    </>,
    slot,
  );
}

QualitySectionCrumb.propTypes = {
  // () => void: the Settings view.
  onOpenSettings: PropTypes.func.isRequired,
  // The title's crumb slot (WorkspaceTitle `crumbRef`), the picker is portalled there.
  slot: PropTypes.object.isRequired,
  // Anything that changes when the sections may have changed (the overview): the observer looks for them again.
  watch: PropTypes.any,
};

export default function QualitySectionMenu({ onOpenSettings, watch = null }) {
  const { current, go } = useQualitySection(watch);

  const click = (event, section) => {
    event.preventDefault();
    if (section.view === 'settings') { onOpenSettings(); return; }
    go(section.key);
  };

  return (
    <nav aria-label="Quality sections" className="quality-sections" data-testid="quality-sections">
      <ul>
        {SECTIONS.map((section) => (
          <li key={section.key}>
            <a
              aria-current={current === section.key && !section.view ? 'location' : undefined}
              className={`quality-section-link${current === section.key && !section.view ? ' is-current' : ''}`}
              href={`#${section.id}`}
              onClick={(event) => click(event, section)}
            >
              {section.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

QualitySectionMenu.propTypes = {
  // () => void: the Settings view.
  onOpenSettings: PropTypes.func.isRequired,
  // Anything that changes when the sections may have changed (the overview): the observer looks for them again.
  watch: PropTypes.any,
};
